/**
 * App update coordination.
 *
 * A new build activates its service worker immediately (registerType: "autoUpdate"),
 * which fires `controllerchange`. From that moment the current tab is running old
 * code against new assets — stale lazy chunks can 404 and routes added in the new
 * build are missing. We therefore reload automatically as soon as it is safe, and
 * fall back to the manual "Update available" toast when it is not.
 */

type Listener = (needsRefresh: boolean) => void;

const listeners = new Set<Listener>();
let needsRefresh = false;

const RELOAD_FLAG = "acex:auto-reloaded-at";
const RELOAD_COOLDOWN_MS = 60_000;

export function subscribeAppUpdate(listener: Listener): () => void {
  listeners.add(listener);
  listener(needsRefresh);
  return () => {
    listeners.delete(listener);
  };
}

function emit() {
  listeners.forEach((l) => l(needsRefresh));
}

function markUpdateAvailable() {
  if (needsRefresh) return;
  needsRefresh = true;
  emit();
}

/** True when reloading would not destroy anything the user is in the middle of. */
export function safeToReload(): boolean {
  if (typeof document === "undefined") return false;
  if (document.visibilityState !== "visible") return true; // background tab: safe

  // Any open dialog/sheet/popover implies an in-progress interaction.
  if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return false;

  // Any typed-into or focused input means unsaved work.
  const fields = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    "input, textarea, select, [contenteditable='true']",
  );
  for (const el of Array.from(fields)) {
    if (el === document.activeElement) return false;
    const value = (el as HTMLInputElement).value;
    if (typeof value === "string" && value.trim() !== "" && el.type !== "hidden") {
      // Ignore prefilled/read-only fields.
      if (!(el as HTMLInputElement).readOnly && !(el as HTMLInputElement).disabled) return false;
    }
  }

  // Live trading surfaces: never yank the page from under an active terminal.
  const path = window.location.pathname;
  if (/\/(spot|futures|forex|binary|p2p)\//.test(path)) return false;

  return true;
}

/** Reload at most once per cooldown window, to make loops impossible. */
export function reloadOnce(reason: string) {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) || 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    /* storage unavailable — still allow a single reload attempt */
  }
  console.info("[app-update] reloading:", reason);
  window.location.reload();
  return true;
}

async function purgeStaleCopy() {
  try {
    const regs = (await navigator.serviceWorker?.getRegistrations()) ?? [];
    await Promise.all(regs.map((r) => r.unregister()));
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch { /* best effort */ }
}

/** Compare the running build with the one currently published. */
async function checkServedVersion() {
  if (import.meta.env.DEV) return;
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return;
    const { build } = await res.json();
    if (!build || build === __APP_BUILD__) return;
    await purgeStaleCopy();
    if (safeToReload() && reloadOnce(`served ${build}, running ${__APP_BUILD__}`)) return;
    markUpdateAvailable();
  } catch { /* offline — try later */ }
}

function tryAutoReload() {
  if (safeToReload()) {
    if (reloadOnce("new version activated")) return;
  }
  markUpdateAvailable();
}

/**
 * Wire up update detection. Call once at startup, after registerSW().
 */
export function initAppUpdate() {
  if (typeof window === "undefined") return;

  navigator.serviceWorker?.addEventListener("controllerchange", tryAutoReload);

  // Recheck when the tab regains focus, and periodically for long sessions.
  const checkForUpdate = () => {
    navigator.serviceWorker?.getRegistration()?.then((reg) => reg?.update().catch(() => {}));
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      checkForUpdate();
      // If an update landed while the tab was hidden, apply it now.
      if (needsRefresh) tryAutoReload();
    }
  });
  window.setInterval(checkForUpdate, 15 * 60 * 1000);

  checkServedVersion();
  window.setInterval(checkServedVersion, 5 * 60 * 1000);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkServedVersion();
  });

  // A stale cached document referencing removed chunks: recover with one reload.
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    reloadOnce("chunk preload failed");
  });
  window.addEventListener("unhandledrejection", (event) => {
    const message = String((event.reason as Error)?.message || event.reason || "");
    if (/dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(message)) {
      reloadOnce("dynamic import failed");
    }
  });
}
